import React, { useState, useCallback } from 'react';
import { Download, Loader2, CheckCircle2 } from 'lucide-react';
import JSZip from 'jszip';
import OutputGallery from './OutputGallery';
import ImagePreviewModal from '../../../../components/ImagePreviewModal';
import Button from '../../../../components/Button';
import { downloadService } from '../../../../services/downloadService';
import type { BrandTeamOutput } from '../../../../services/brandTeamService';

interface StepResultsProps {
  outputs: BrandTeamOutput[];
  onNewSession: () => void;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

async function fetchAsBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch file: ${res.status}`);
  }
  return res.blob();
}

const StepResults: React.FC<StepResultsProps> = ({ outputs, onNewSession }) => {
  const [previewOutput, setPreviewOutput] = useState<BrandTeamOutput | null>(null);
  const [zipping, setZipping] = useState(false);

  const avgQa = outputs.length > 0
    ? Math.round(outputs.reduce((sum, o) => sum + (o.qaScore || 0), 0) / outputs.length)
    : 0;

  const handleDownloadSingle = useCallback(async (output: BrandTeamOutput) => {
    const category = (output.metadata?.category as string) || 'mockup';
    const filename = `${category.replace(/\s+/g, '-')}-${output.id.slice(0, 8)}.png`;

    await downloadService.downloadFileFromUrl(output.outputUrl, filename, {
      useProxy: true,
      format: 'png',
    });
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (outputs.length === 0) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder('brand-team-mockups');
      await Promise.all(
        outputs.map(async (output, i) => {
          const category = (output.metadata?.category as string) || 'mockup';
          const filename = `${String(i + 1).padStart(2, '0')}-${category.replace(/\s+/g, '-')}-${output.id.slice(0, 8)}.png`;
          let blob: Blob;
          try {
            blob = await fetchAsBlob(output.outputUrl);
          } catch {
            const response = await fetch(`/api/download/external/png?sourceUrl=${encodeURIComponent(output.outputUrl)}`, {
              credentials: 'include',
            });
            if (!response.ok) {
              throw new Error(`Failed to fetch file: ${response.status}`);
            }
            blob = await response.blob();
          }
          folder!.file(filename, blob);
        })
      );
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(zipBlob, 'brand-team-mockups.zip');
    } finally {
      setZipping(false);
    }
  }, [outputs]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      {/* Header with stats */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-text-dark">Your Mockups</h2>
          <p className="mt-1 text-sm text-body-grey">
            {outputs.length} outputs generated
            {avgQa > 0 && <> &middot; Average QA score: <span className={`font-semibold ${avgQa >= 80 ? 'text-green-600' : avgQa >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{avgQa}</span></>}
          </p>
        </div>
        {outputs.length > 0 && (
          <button
            type="button"
            onClick={handleDownloadAll}
            disabled={zipping}
            className="inline-flex items-center gap-2 rounded-xl border border-primary-100 bg-white px-4 py-2.5 text-xs font-semibold text-text-dark shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {zipping ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Zipping...</>
            ) : (
              <><Download className="h-4 w-4" /> Download All</>
            )}
          </button>
        )}
      </div>

      {/* Auto-save notice */}
      {outputs.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
          <span>All mockups are automatically saved to your <a href="/dashboard/library" className="font-semibold underline hover:text-emerald-900">Library</a></span>
        </div>
      )}

      {/* Gallery */}
      <OutputGallery
        outputs={outputs}
        totalExpected={outputs.length}
        isGenerating={false}
        onPreview={setPreviewOutput}
        onDownload={handleDownloadSingle}
      />

      {/* Preview Modal */}
      {previewOutput && (
        <ImagePreviewModal
          src={previewOutput.outputUrl}
          alt={(previewOutput.metadata?.category as string) || 'Mockup'}
          title={(previewOutput.metadata?.category as string) || 'Mockup Preview'}
          subtitle={previewOutput.qaScore != null ? `QA Score: ${previewOutput.qaScore}` : undefined}
          onClose={() => setPreviewOutput(null)}
          onDownload={() => handleDownloadSingle(previewOutput)}
        />
      )}

      {/* Bottom action */}
      <div className="mt-8 flex justify-end">
        <Button variant="primary" size="large" onClick={onNewSession}>
          Start New Session
        </Button>
      </div>
    </div>
  );
};

export default StepResults;
